/**
 * Invoice line matching: what the synchronous matching does, plus the AI kill-switch and tools.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { Button, Loader, Paper, Toggle } from '@insio/ui-toolkit';
import { AutomationKind } from '@insio/shared-types';
import {
  InvoiceMatchConfirmationSettingsDocument,
  UpdateInvoiceMatchConfirmationSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { AiToolsList } from './AiToolsList';
import { AutomationHelpModal } from './AutomationHelpModal';
import { AutomationStepHeading } from './AutomationStepHeading';
import { useAutomationHelp } from './use-automation-help';
import { useCanChangeAutomationSettings } from './use-can-change-automation-settings';

export function InvoiceMatchConfirmationSettingsSection() {
  const t = useFeatureTranslation('systemSettings.automation.matching');
  const tHelp = useFeatureTranslation('systemSettings.automation.help');
  const { showError } = useToastHelpers();
  const canChange = useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(InvoiceMatchConfirmationSettingsDocument);
  const {
    help,
    loading: helpLoading,
    error: helpError
  } = useAutomationHelp(AutomationKind.INVOICE_MATCH_CONFIRMATION);
  // The response carries no id to normalise, so the settings query is refetched —
  // otherwise the lifecycle diagram keeps showing the pre-save state.
  const [updateSettings] = useMutation(UpdateInvoiceMatchConfirmationSettingsDocument, {
    refetchQueries: [{ query: InvoiceMatchConfirmationSettingsDocument }],
    awaitRefetchQueries: true
  });

  const [isEnabled, setIsEnabled] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const settings = data?.invoiceMatchConfirmationSettings;

  useEffect(() => {
    if (settings) {
      setIsEnabled(settings.isEnabled);
    }
  }, [settings]);

  /** The switch is stored the moment it flips; a refused write flips it back. */
  const handleEnabledChange = async (next: boolean) => {
    if (!settings || !canChange) return;
    setIsEnabled(next);
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({
        variables: { input: { isEnabled: next } },
        errorPolicy: 'none'
      });
    } catch {
      setIsEnabled(settings.isEnabled);
      showError(t('saveError'));
    }
  };

  if ((loading && !settings) || (helpLoading && !help)) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  return (
    <div className='space-y-lg'>
      <AutomationStepHeading title={t('title')} description={t('description')} />

      <Paper heading='stripe' bleed padding='none' testId='matching-ai-section'>
        <Paper.Title>
          <h2>{t('ai.title')}</h2>
        </Paper.Title>
        <Paper.Action>
          <Toggle
            checked={isEnabled}
            onChange={handleEnabledChange}
            disabled={!canChange}
            labelText={isEnabled ? t('enabled.on') : t('enabled.off')}
            testId='automation-enabled-toggle'
          />
        </Paper.Action>
        <Paper.Content className='space-y-md'>
          <p className='text-sm text-foreground-muted'>{t('ai.description')}</p>
          {helpError ? (
            <p className='text-sm text-error-text' data-testid='matching-ai-tools-error'>
              {tHelp('loadError')}
            </p>
          ) : (
            <AiToolsList
              title={t('tools.title')}
              description={t('tools.description')}
              items={help?.tools.map(tool => tool.label) ?? []}
              isBesideField={false}
              className='lg:w-96'
              testId='matching-ai-tools'
            />
          )}

          <Button
            variant='ghost'
            icon='circle-info'
            onClick={() => setIsHelpOpen(true)}
            testId='invoice-matching-help-open'
          >
            {tHelp('open')}
          </Button>
        </Paper.Content>
      </Paper>

      <AutomationHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        kind={AutomationKind.INVOICE_MATCH_CONFIRMATION}
        title={t('ai.title')}
      />
    </div>
  );
}
